import type { ChangeEventHandler } from "react"
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react"

import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined"
import AddIcon from "@mui/icons-material/Add"
import { useSelector } from "@ggs/store"
import { get } from "lodash"
import { SvgIcon } from ".."
import { useUserLocation } from "../../local/hooks"
import { type FORM_VALUES, BUDGET_OPTIONS } from "./_data"
import {
  AutocompletePopover,
  Input,
  InputClearBtn,
  InputContainer,
  InputControl,
  InputIcon,
  InputLabel,
  ListItem,
  ListTitle,
} from "./_styles"

import { offset, shift, size } from "@floating-ui/react"
import { Unstable_Popup as BasePopup } from "@mui/base/Unstable_Popup"
import { OutlinedInput, useMediaQuery, InputAdornment } from "@mui/material"
import { styled, useTheme } from "@mui/material/styles"
import { useFocusWithin } from "react-aria"
import { useController, useFormContext } from "react-hook-form"
import { fontSize } from "@mui/system"

type BudgetInputProps = {
  variant?: string
}
const BudgetInput = forwardRef<HTMLDivElement, BudgetInputProps>(({ variant }, ref) => {
  // Get budget range from trip search options in the store
  const { mapCountryToCurrency } = useUserLocation()
  const userCountry = useSelector((state) => state.settings.country)
  const currency = mapCountryToCurrency(userCountry.code ?? "US", userCountry.isEurope ?? false)
  const tripSearch = useSelector((state) => state.views.index.tripSearch)
  const budgetMin = get(tripSearch, "options.pricesMinMax.min", 0) // Minimum budget is 0
  const budgetMax = get(tripSearch, "options.pricesMinMax.max", 999999) // Maximum budget is 999999 (arbitrarily large value)

  // Form context hooks
  const { resetField } = useFormContext<FORM_VALUES>()
  const {
    field: { name, value, onChange, onBlur, ref: inputRef },
    fieldState: { isDirty },
  } = useController({
    name: "budget",
  })
  const [inputValue, setInputValue] = useState(value)
  const [selectedSymbol, setSelectedSymbol] = useState<JSX.Element | null>(null)
  // Disable budget options that are outside the budget range
  const renderedOptions = useMemo(() => {
    return BUDGET_OPTIONS.map((option, index) => {
      const minCurrency = Intl.NumberFormat(`en-${userCountry.code ?? "CA"}`, {
        style: "currency",
        currencyDisplay: "narrowSymbol",
        currency,
        maximumFractionDigits: 0,
      }).format(option.min)
      const maxCurrency = Intl.NumberFormat(`en-${userCountry.code ?? "CA"}`, {
        style: "currency",
        currencyDisplay: "narrowSymbol",
        currency,
        maximumFractionDigits: 0,
      }).format(option.max)

      const label = option.label.replace("XXX", minCurrency).replace("YYY", maxCurrency)
      const value = `${option.min}-${option.max}`

      // Generate the symbol by repeating the MonetizationOnOutlinedIcon based on the index
      const symbol = (
        <div style={{ display: "flex", alignItems: "center" }}>
          {/* Loop to render $ symbols */}
          {[...Array(index === BUDGET_OPTIONS.length - 1 ? index + 1 : index + 2)].map((_, i) => (
            <svg
              key={i}
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              style={{
                pointerEvents: "none",
              }}
            >
              <path
                d="M8.5 14.6667C8.5 15.9553 9.54467 17 10.8333 17H13C14.3807 17 15.5 15.8807 15.5 14.5C15.5 13.1193 14.3807 12 13 12H11C9.61929 12 8.5 10.8807 8.5 9.5C8.5 8.11929 9.61929 7 11 7H13.1667C14.4553 7 15.5 8.04467 15.5 9.33333M12 5.5V7M12 17V18.5M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z"
                stroke="#464446"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{
                  pointerEvents: "none",
                  fill: "none",
                  transition: "none",
                }}
              />
            </svg>
          ))}

          {/* Add "+" symbol only for the last option */}
          {index === BUDGET_OPTIONS.length - 1 && <AddIcon style={{ fontSize: "18px" }} />}
        </div>
      )
      return {
        ...option,
        label,
        value,
        disabled: budgetMin > option.max || budgetMax < option.min,
        symbol,
      }
    })
  }, [budgetMin, budgetMax, currency])

  // Popper hooks
  const containerRef = useRef<HTMLDivElement | null>(null)
  // const containerRef = createRef(ref)
  const [budgetPopupAnchor, setBudgetPopupAnchor] = useState<HTMLElement | null>(null)
  const [isFocusWithin, setFocusWithin] = useState(false)
  const [hasBlurred, setHasBlurred] = useState(false)
  const { focusWithinProps } = useFocusWithin({
    onFocusWithin: () => setBudgetPopupAnchor(containerRef.current),
    onBlurWithin: () => {
      setBudgetPopupAnchor(null)
      if (value !== "") setHasBlurred(true)
    },
    onFocusWithinChange: (isFocusWithin) => setFocusWithin(isFocusWithin),
  })
  const budgetPopupOpen = Boolean(budgetPopupAnchor)
  const budgetPopupId = budgetPopupOpen ? "budget-popover" : undefined
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"))

  // Handle input change
  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    onChange(event)
    setBudgetPopupAnchor(event.target.value.length > 0 ? null : containerRef.current)
  }

  // Handle clear button click
  const handleClear = () => {
    resetField(name)
    setInputValue("")
    setBudgetPopupAnchor(null)
    setSelectedSymbol(null)
    setFocusWithin(false)
  }

  // Handle list item click in the dropdown popover
  const handleItemClick = (option: (typeof renderedOptions)[number]) => {
    // if disabled, do nothing
    if (option.disabled) return
    // if already selected, clear the value, otherwise set the value
    if (option.label === inputValue) {
      resetField(name)
      setInputValue("")
      setSelectedSymbol(null)
    } else {
      onChange(option.value)
      setInputValue(option.label)
      setSelectedSymbol(option.symbol)
      setBudgetPopupAnchor(null)
      containerRef.current?.focus()
    }
  }

  useImperativeHandle(
    ref,
    // @ts-ignore
    () => {
      return {
        focus() {
          containerRef.current?.focus()
        },
      }
    },
    []
  )

  return (
    <>
      <InputContainer
        ref={containerRef}
        focused={isFocusWithin}
        variant={variant}
        {...focusWithinProps}
        onClick={() => {
          setFocusWithin(true)
          if (budgetPopupAnchor) return
          setBudgetPopupAnchor(containerRef.current)
        }}
        tabIndex={0}
      >
        <InputIcon>
          <SvgIcon variant="tag" size={24} />
        </InputIcon>
        <InputControl variant={variant}>
          <InputLabel htmlFor="input-budget">Budget</InputLabel>
          <OutlinedInput
            ref={inputRef}
            id="input-budget"
            name={name}
            placeholder={selectedSymbol ? "" : "Select price per person"}
            autoComplete="off"
            spellCheck={false}
            value={""}
            onChange={handleChange}
            onBlur={onBlur}
            disabled
            startAdornment={
              selectedSymbol && <InputAdornment position="start">{selectedSymbol}</InputAdornment>
            }
            sx={{
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },

              "& .MuiInputBase-input": {
                fontFamily: "Inter Variable, sans-serif",
                fontSize: "18px",
                fontWeight: 400,
                lineHeight: 1.6,
                letterSpacing: "0px",
                padding: "0px",
                margin: "0px",
                color: "rgba(84, 84, 84,1) !important",
              },
              "& .MuiInputBase-input.Mui-disabled::placeholder": {
                "-webkit-text-fill-color": "rgba(84, 84, 84,0.8) !important",
                "opacity": 1,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "padding": "0px",
            }}
          />
        </InputControl>
        {value !== "" && isDirty && hasBlurred && (
          <InputClearBtn
            type="reset"
            focused={isFocusWithin}
            onClick={(e) => {
              e.stopPropagation()
              handleClear()
            }}
            onMouseDown={(e) => {
              e.preventDefault()
              e.currentTarget.blur()
            }}
          >
            <span>Clear</span>
            <SvgIcon variant="close" size={8} strokeMode />
          </InputClearBtn>
        )}
        <BasePopup
          id={budgetPopupId}
          open={budgetPopupOpen}
          anchor={budgetPopupAnchor}
          placement={isDesktop && variant === "default" ? "top" : "bottom"}
          strategy="fixed"
          disablePortal
          middleware={[
            shift(),
            offset(isDesktop ? 10 : 4),
            size({
              apply({ rects, availableHeight, elements }) {
                Object.assign(elements.floating.style, {
                  minWidth: `${rects.reference.width}px`,
                  height: isDesktop ? "" : `${availableHeight - 98}px`,
                  overflow: availableHeight >= elements.floating.clientHeight ? "" : "auto",
                })
              },
            }),
          ]}
          slotProps={{
            root: {
              role: "listbox",
              style: {
                zIndex: 1,
              },
            },
          }}
        >
          <AutocompletePopover
            sx={(theme) => ({
              display: "flex",
              flexFlow: "column",
              gridGap: theme.spacing(4),
              pt: theme.spacing(6),
            })}
          >
            <ListTitle>Price ({currency}) per person</ListTitle>
            {renderedOptions.map((option) => (
              <BudgetListItem
                key={option.label}
                tabIndex={option.disabled ? -1 : 0}
                disabled={option.disabled}
                role="option"
                aria-selected={option.label === inputValue}
                selected={option.label === inputValue}
                onClick={() => handleItemClick(option)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleItemClick(option)
                  }
                }}
              >
                <div className="list-item__label">{option.symbol}</div>
                {/* <div className="list-item__tag">{option.tag}</div> */}
              </BudgetListItem>
            ))}
          </AutocompletePopover>
        </BasePopup>
      </InputContainer>
    </>
  )
})
BudgetInput.displayName = "BudgetInput"

export default BudgetInput

const BudgetListItem = styled(ListItem)(({ theme }) => ({
  ".list-item__label": {
    marginRight: "auto",
  },
  ".list-item__message": {
    ...theme.typography["body.sm"],
  },
  ".list-item__tag": {
    ...theme.typography["body.sm"],
    opacity: 0.7,
  },
}))
